import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardActionArea, Avatar, Stack, Badge
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface UserData {
  id: string;
  name: string;
  email: string;
}

interface TaskData {
  id: string;
  markdownId: string;
  userId: string;
  status: '未着手' | '進行中' | '完了';
  startedAt?: string;
  finishedAt?: string;
}

const UserSelectList: React.FC = () => {
  const [userList, setUserList] = useState<UserData[]>([]);
  const [taskList, setTaskList] = useState<TaskData[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const savedUsers = localStorage.getItem('user-list');
    const savedTasks = localStorage.getItem('task-list');
    if (savedUsers) setUserList(JSON.parse(savedUsers));
    if (savedTasks) setTaskList(JSON.parse(savedTasks));
  }, []);

  const handleUserClick = (userId: string) => {
    navigate(`/user-tasks/${userId}`);
  };

  const isToday = (isoDate?: string) => {
    if (!isoDate) return false;
    const date = new Date(isoDate);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getUserStats = (userId: string) => {
    const userTasks = taskList.filter(task => task.userId === userId);
    const running = userTasks.filter(t => t.status === '進行中').length;
    const notStarted = userTasks.filter(t => t.status === '未着手').length;
    const finishedToday = userTasks.filter(t => t.status === '完了' && isToday(t.finishedAt)).length;
    return { running, notStarted, finishedToday };
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        ユーザーを選択
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={3} mt={3}>
        {userList.map(user => {
          const stats = getUserStats(user.id);
          return (
            <Box key={user.id} width={{ xs: '100%', sm: '48%', md: '30%' }}>
              <Card
                elevation={3}
                sx={{
                  borderRadius: 2,
                  transition: '0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}
              >
                <CardActionArea onClick={() => handleUserClick(user.id)} sx={{ p: 2 }}>
                  <Stack spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" align="center">{user.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Badge badgeContent={stats.running} color="info" showZero>
                        <PlayArrowIcon />
                      </Badge>
                      <Badge badgeContent={stats.notStarted} color="warning" showZero>
                        <HourglassEmptyIcon />
                      </Badge>
                      <Badge badgeContent={stats.finishedToday} color="success" showZero>
                        <CheckCircleIcon />
                      </Badge>
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Container>
  );
};

export default UserSelectList;